import { EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from "antd";
import { useEffect, useState, useCallback } from "react";
import RolePermissionModal from "./RolePermissionModal";
import apiService from "../../../services/apiService";
import { getLocalStorageItem } from "../../../utils/Validation";
import { debounce } from "lodash";
import { usePermissions } from "../../../context/PermissionContext";
import styles from "../manageRole.module.css";

interface TableParams {
  pagination: TablePaginationConfig;
}

interface PermissionAction {
  view?: string;
  delete?: string;
  update?: string;
  assign?: string;
}

interface Permissions {
  manage_lead?: PermissionAction;
  manage_roles?: PermissionAction;
  [key: string]: PermissionAction | undefined;
}

export interface Role {
  id: number;
  role_name: string;
  createdAt: string;
  updatedAt: string;
  team_name: string | null;
  userCount: number;
  permissions: Permissions;
}

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalRoles: number;
  totalPages: number;
}

export interface RolesResponse {
  message: string;
  roles: Role[];
  pagination: Pagination;
}

interface RoleTabProps {
  searchText: string;
}

const RoleTab = ({ searchText }: RoleTabProps) => {
  const userData = getLocalStorageItem("detail");

  const [roles, setRoles] = useState<Role[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalRoles: 0,
  });
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      position: ["bottomRight"],
      showTotal: (total: number, range: number[]) =>
        `Showing ${range[0]} - ${range[1]} of ${total} results`,
    },
  });
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [userId, setUserId] = useState<string | number>();

  // const fetchRoles = async (page = 1, pageSize = 10, search = "") => {
  //     setLoading(true);
  //     try {
  //         const endpoint = `http://localhost:8080/roles?page=${page}&pageSize=${pageSize}&userId=${userData.userId}${search ? `&search=${search}` : ""}`;
  //         const response: RolesResponse = await apiService.get(endpoint);

  //         if (response.roles) {
  //             setRoles(response.roles);
  //             setTableParams((prev) => ({
  //                 ...prev,
  //                 pagination: {
  //                     ...prev.pagination,
  //                     current: response.pagination.currentPage,
  //                     pageSize: response.pagination.pageSize,
  //                     total: response.pagination.totalRoles,
  //                 },
  //             }));
  //         }
  //     } catch (error) {
  //         console.error("Error fetching roles:", error);
  //     } finally {
  //         setLoading(false);
  //     }
  // };

  // Debounced version of fetchRoles

  const fetchRoles = async (page = 1, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const endpoint = `/roles?page=${page}&pageSize=${pageSize}&userId=${
        userData.userId
      }${search ? `&search=${search}` : ""}`;
      const response: RolesResponse = await apiService.get(endpoint);

      if (response.roles) {
        setRoles(response.roles);
        setTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            current: response.pagination.currentPage,
            pageSize: response.pagination.pageSize,
            total: response.pagination.totalRoles, // ✅ Ensure total is updated
            showTotal: (total: number, range: number[]) =>
              `Showing ${range[0]} - ${range[1]} of ${total} results`, // ✅ Ensure this function is always included
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchRoles = useCallback(debounce(fetchRoles, 500), []);

  useEffect(() => {
    debouncedFetchRoles(1, pagination.pageSize, searchText);
  }, [searchText]);

  useEffect(() => {
    fetchRoles(1, pagination.pageSize);
  }, []);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...pagination,
        position: ["bottomRight"],
        showTotal: (total: number, range: number[]) =>
          `Showing ${range[0]} - ${range[1]} of ${total} results`,
      },
    }));
    fetchRoles(pagination.current || 1, pagination.pageSize || 10, searchText);
  };

  const openModal = (id: string | number) => {
    setOpenEditModal(true);
    setUserId(id);
  };
  const closeModal = () => setOpenEditModal(false);

  const { permissions } = usePermissions();
  const roleId = permissions?.role?.id;
  const canRoleEditAll =
    permissions?.detailedPermissions?.manage_roles?.update === "all";
  const canRoleEditAssigned =
    permissions?.detailedPermissions?.manage_roles?.update === "assigned";

  const hasRolePermissionToEdit = (user: any) => {
    const userRoleId = user?.id;
    if (canRoleEditAll) {
      return true;
    }
    if (userRoleId === roleId && canRoleEditAssigned) {
      return true;
    }
    return false;
  };

  const columns = [
    {
      title: "CREATED ON",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString("en-GB"),
    },
    {
      title: "UPDATED ON",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date: string) => new Date(date).toLocaleDateString("en-GB"),
    },
    {
      title: "ROLE",
      dataIndex: "role_name",
      key: "role_name",
    },
    {
      title: "TEAM",
      dataIndex: "team_name",
      key: "team_name",
      render: (team: string | null) =>
        team ? (
          <Tag className={`${styles.teamTag} ${styles.teamTagPurple}`}>
            {team}
          </Tag>
        ) : (
          <span className={styles.mutedCell}>N/A</span>
        ),
    },
    {
      title: "NO OF USERS",
      dataIndex: "userCount",
      key: "userCount",
      render: (count: number) => <span className={styles.primaryCell}>{count}</span>,
    },
    {
      title: "ACTION",
      key: "action",
      render: (_: any, record: Role) => {
        const hasPermission = hasRolePermissionToEdit(record);
        return (
          <>
            {hasPermission && (
              <Tooltip title="Edit Role">
                <Button
                  type="text"
                  className={styles.editButton}
                  icon={<EditOutlined />}
                  onClick={() => openModal(record.id)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Col className={styles.tableColumn}>
      <Card className={styles.tableCard} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          rowKey="id"
          dataSource={roles}
          loading={loading}
          pagination={tableParams.pagination}
          onChange={handleTableChange}
          className={styles.manageTable}
          rowClassName={() => styles.tableRow}
          scroll={{ x: 900 }}
        />
      </Card>
      <RolePermissionModal
        refreshList={() => fetchRoles()}
        isEdit={true}
        id={userId}
        closeModal={closeModal}
        openEditModal={openEditModal}
      />
    </Col>
  );
};

export default RoleTab;
